import React, { useCallback, useEffect, useRef, useState } from "react";
import { Unity, useUnityContext } from "react-unity-webgl";
import VoiceRecorder from "./VoiceRecorder";
import Sidebar from "./Sidebar";
import { Box, Typography, useMediaQuery } from "@mui/material";
import EnvironmentMap from "./EnvironmentMap";
import Label from "./Label";
import LoadingAnimation from "./LoadingAnimation";
import LoadingSequence from "./LoadingSequence";
import MicAccessPopup from "./MicAccessPopup";
import StartGuideDialog from "./StartGuideDialog";
import ExperiencesAPI from "../services/ExperiencesAPI";
import { animated } from "@react-spring/web";
import { useTheme } from "@mui/material/styles";

const UnityWebViewer = ({ experienceId, embed }) => {
  const buildPrefix = "/webplayer/10kScienceWeb_1.2.24_Production";
  const voiceRecorderRef = useRef(null);
  const [currentElement, setCurrentElement] = useState(null);
  const [systemData, setSystemData] = useState(null);
  const [focusedElement, setFocusedElement] = useState(null);
  const [labelPosition, setLabelPosition] = useState(null);
  const [labelExpaneded, setLabelExpanded] = useState(false);
  const [currentEnvironment, setCurrentEnvironment] = useState(null);
  const [systemLoaded, setSystemLoaded] = useState(false);
  const [experienceData, setExperienceData] = useState(null);
  const [layoutOverridesAll, setLayoutOverridesAll] = useState(null);
  const [layoutOverrides, setLayoutOverrides] = useState(null);
  const [cursorStyle, setCursorStyle] = useState("default");
  const focusTimeoutRef = useRef(null);
  const expandedTimeoutRef = useRef(null);
  const latestMousePositionRef = useRef({ x: 0, y: 0 });
  const [environmentsVisited, setEnvironmentsVisited] = useState(0);
  const [currentInsight, setCurrentInsight] = useState(null);
  const [instructionsVisible, setInstructionVisible] = useState(false);
  const [showControlsOverlay, setShowControlsOverlay] = useState(false);
  const [edgeHover, setEdgeHover] = useState({
    left: false,
    right: false,
    top: false,
    bottom: false,
  });
  const edgeHoverRef = useRef({
    left: false,
    right: false,
    top: false,
    bottom: false,
  });

  const [showEnvironmentLabel, setShowEnvironmentLabel] = useState(false);
  const environmentLabelTimeoutRef = useRef(null);

  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"));
  const overlayWidth = isPhone ? "92vw" : isTablet ? "70vw" : "540px";
  const overlayPadding = isPhone ? "6px 12px" : "8px 16px";
  const topMessageOffset = isPhone ? "70px" : "90px";
  const envLabelBottom = isPhone ? "100px" : "150px";
  const edgeControlWidth = isPhone ? 48 : 60;
  const edgeControlHeight = isPhone ? 46 : 54;

  // ---- WebGPU debug hook (Chromebook whitescreen) ----
  const webGpuDebugInitRef = useRef(false);

  useEffect(() => {
    if (webGpuDebugInitRef.current) return;
    webGpuDebugInitRef.current = true;

    const debugOn = new URLSearchParams(window.location.search).has(
      "wgpuDebug"
    );
    if (!debugOn) return;

    // Surface Dawn/WebGPU validation rejections Unity might not log
    const onRejection = (e) => {
      console.error("[WebGPU] Unhandled rejection:", e?.reason || e);
    };
    window.addEventListener("unhandledrejection", onRejection);

    (async () => {
      try {
        if (!("gpu" in navigator)) return;
        const adapter = await navigator.gpu.requestAdapter();
        if (!adapter) return;

        const device = await adapter.requestDevice();
        // Keep a ref so you can inspect in DevTools
        window.__wgpuDebugDevice = device;

        device.lost.then((info) => {
          console.error(
            "[WebGPU] Device lost:",
            info?.message || "(no message)"
          );
        });

        console.log("[WebGPU] Adapter:", adapter?.name || "(unknown)");
        console.log("[WebGPU] Limits:", device.limits);
      } catch (err) {
        console.warn("[WebGPU] Debug hook failed:", err);
      }
    })();

    return () => {
      window.removeEventListener("unhandledrejection", onRejection);
    };
  }, []);

  const {
    unityProvider,
    sendMessage,
    addEventListener,
    removeEventListener,
    isLoaded,
  } = useUnityContext({
    loaderUrl: `${buildPrefix}.loader.js`,
    dataUrl: `${buildPrefix}.data`,
    frameworkUrl: `${buildPrefix}.framework.js`,
    codeUrl: `${buildPrefix}.wasm`,
  });

  const playerOffset = 0;
  const dragStart = useRef(null);

  const sendMessageToUnity = (gameObject, methodName, value) => {
    console.log(unityProvider);
    sendMessage(gameObject, methodName, value);
  };

  const handleElementSelected = useCallback((elementData, selectionPoint) => {
    const parsedElement =
      typeof elementData === "string" ? JSON.parse(elementData) : elementData;
    // console.log(`ELEMENT SELECTED ${parsedElement.labelData.displayName}`);
    //        console.log(parsedElement.labelData.displayName, parsedElement)
    setCurrentElement(parsedElement);
  }, []);

  const handleInsightStarted = useCallback((insightId) => {
    ExperiencesAPI.get(`insights/${insightId}`)
      .then((response) => {
        console.log("INSIGHT DATA", response);
        setCurrentInsight(response.data.data);
      })
      .catch((error) => console.log(error));
  }, []);

  const handleAssessmentComplete = useCallback((assessmentId) => {
    console.log("ASSESSSSSSS", assessmentId);
    setTimeout(() => {
      ExperiencesAPI.get(`assessments/${assessmentId}`)
        .then((response) => {
          console.log("ASSESSMENT", response);
        })
        .catch((error) => console.log(error));
    }, 3000);
  }, []);

  const handleInstructionVisibilityChanged = useCallback((visible) => {
    console.log("INSTRUCTIONS", visible);
    setInstructionVisible(visible.toLowerCase() === "true");
  }, []);

  const handleEnvironmentChanged = useCallback((environmentData) => {
    console.log("ENVIRONMENT CHANGED");
    setEnvironmentsVisited((prev) => {
      const next = prev + 1;
      // Show overlay only after the third environment loads (first change is auto on start).
      setShowControlsOverlay(next === 3);
      return next;
    });

    try {
      const parsedEnvironment =
        typeof environmentData === "string"
          ? JSON.parse(environmentData)
          : environmentData;
      setFocusedElement(null);
      console.log(parsedEnvironment);
      setCurrentEnvironment(parsedEnvironment);

      // --- New: Clear any existing timeout before setting a new one ---
      if (environmentLabelTimeoutRef.current) {
        clearTimeout(environmentLabelTimeoutRef.current);
      }

      setShowEnvironmentLabel(true);

      environmentLabelTimeoutRef.current = setTimeout(() => {
        setShowEnvironmentLabel(false);
        environmentLabelTimeoutRef.current = null; // clear ref after
      }, 2500);
    } catch (error) {
      console.error("Failed to parse environmentData", error);
    }
  }, []);

  const handleRecordingStarted = useCallback((instanceId) => {
    if (voiceRecorderRef.current) {
      voiceRecorderRef.current.startRecording(instanceId);
    }
  }, []);

  const handleUploadAudio = useCallback((presignedUrl) => {
    if (voiceRecorderRef.current) {
      voiceRecorderRef.current.uploadFileToS3(presignedUrl);
    }
  }, []);

  const handleReadyToLoadExperience = useCallback(
    (message) => {
      console.log("LOADING EXPERIENCE", experienceId);
      sendMessageToUnity("EventDispatcher", "LoadExperience", experienceId);
    },
    [isLoaded, experienceId]
  );

  const handleSystemLoaded = useCallback((systemData) => {
    const parsedSystem =
      typeof systemData === "string" ? JSON.parse(systemData) : systemData;
    console.log("SYSTEM DATA", parsedSystem);
    setSystemData(parsedSystem);
  }, []);

  const handleSystemFullyLoaded = useCallback(() => {
    console.log("SYSTEM IS NOW LOADED");
    setSystemLoaded(true);
  }, []);

  const handleElementDeselected = useCallback((message) => {
    //  console.log("Element deselect");
    setCurrentElement(null);
  }, []);

  useEffect(() => {
    addEventListener("elementSelected", handleElementSelected);
    addEventListener("elementDeselected", handleElementDeselected);
    addEventListener("environmentChanged", handleEnvironmentChanged);
    addEventListener("recordingStarted", handleRecordingStarted);
    addEventListener("uploadAudioFile", handleUploadAudio);
    addEventListener("readyToLoadExperience", handleReadyToLoadExperience);
    addEventListener("systemLoaded", handleSystemLoaded);
    addEventListener("systemFullyLoaded", handleSystemFullyLoaded);
    addEventListener("assessmentComplete", handleAssessmentComplete);
    addEventListener(
      "instructionVisibilityChanged",
      handleInstructionVisibilityChanged
    );
    addEventListener("insightStarted", handleInsightStarted);

    return () => {
      removeEventListener("elementSelected", handleElementSelected);
      removeEventListener("elementDeselected", handleElementDeselected);
      removeEventListener("environmentChanged", handleEnvironmentChanged);
      removeEventListener("recordingStarted", handleRecordingStarted);
      removeEventListener("uploadAudioFile", handleUploadAudio);
      removeEventListener("readyToLoadExperience", handleReadyToLoadExperience);
      removeEventListener("systemLoaded", handleSystemLoaded);
      removeEventListener("systemFullyLoaded", handleSystemFullyLoaded);
      removeEventListener("assessmentComplete", handleAssessmentComplete);
      removeEventListener(
        "instructionVisibilityChanged",
        handleInstructionVisibilityChanged
      );
      removeEventListener("insightStarted", handleInsightStarted);
    };
  }, [
    addEventListener,
    removeEventListener,
    handleElementSelected,
    handleElementDeselected,
    handleEnvironmentChanged,
    handleSystemLoaded,
    handleSystemFullyLoaded,
    handleRecordingStarted,
    handleUploadAudio,
    handleReadyToLoadExperience,
    handleAssessmentComplete,
    handleInstructionVisibilityChanged,
    handleInsightStarted,
    isLoaded,
  ]);

  useEffect(() => {
    if (experienceId) {
      ExperiencesAPI.get(`experiences/${experienceId}`).then((response) => {
        console.log("EXPERIENCE DATA", response);
        setExperienceData(response.data);
      });
    }
  }, [experienceId]);

  // Load optional local layout overrides for the environment map.
  useEffect(() => {
    const loadOverrides = async () => {
      try {
        const res = await fetch("/environmentLayout.json", {
          cache: "no-cache",
        });
        if (!res.ok) return;
        const data = await res.json();
        setLayoutOverridesAll(data);
      } catch (err) {
        console.warn("Failed to load environment layout overrides", err);
      }
    };
    loadOverrides();
  }, []);

  // Select overrides for the current system id, if available.
  useEffect(() => {
    if (!layoutOverridesAll || !systemData?.id) {
      setLayoutOverrides(null);
      return;
    }
    const overridesForSystem = layoutOverridesAll[systemData.id];
    setLayoutOverrides(
      overridesForSystem && typeof overridesForSystem === "object"
        ? overridesForSystem
        : null
    );
  }, [layoutOverridesAll, systemData]);

  useEffect(() => {
    const handleMouseMove = (event) => {
      const unityContainer = document.querySelector(".content"); // or whatever your outer div class is
      if (!unityContainer) return;

      const rect = unityContainer.getBoundingClientRect();
      const headerHeight = parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue(
          "--app-header-height"
        )
      );
      const resolvedHeaderHeight = Number.isFinite(headerHeight)
        ? headerHeight
        : 0;
      const edgeHoverWidth = 120;
      const edgeHoverHeight = 108;

      latestMousePositionRef.current = {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
      };

      const x = latestMousePositionRef.current.x;
      const y = latestMousePositionRef.current.y;
      const contentHeight = Math.max(0, rect.height - resolvedHeaderHeight);
      const yInUnity = y - resolvedHeaderHeight;
      const withinUnity = yInUnity >= 0 && yInUnity <= contentHeight;

      const nextHover = {
        left: withinUnity && x <= edgeHoverWidth,
        right: withinUnity && x >= rect.width - edgeHoverWidth,
        top: withinUnity && yInUnity <= edgeHoverHeight,
        bottom: withinUnity && yInUnity >= contentHeight - edgeHoverHeight,
      };

      const prevHover = edgeHoverRef.current;
      if (
        prevHover.left !== nextHover.left ||
        prevHover.right !== nextHover.right ||
        prevHover.top !== nextHover.top ||
        prevHover.bottom !== nextHover.bottom
      ) {
        edgeHoverRef.current = nextHover;
        setEdgeHover(nextHover);
      }
    };

    window.addEventListener("mousemove", handleMouseMove);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
    };
  }, []);

  useEffect(() => {
    const handleMouseMove = (event) => {
      if (currentElement && currentElement.targetEnvironmentID) {
        // console.log("ZOOM IN")
        setCursorStyle("zoom-in");
      } else if (currentElement) {
        // console.log("GRAB");
        setCursorStyle("pointer");
      } else {
        // console.log("ARROW");
        setCursorStyle("move");
      }
    };

    window.addEventListener("mousemove", handleMouseMove);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
    };
  }, [currentElement]);

  const handleMouseDown = (event) => {
    dragStart.current = { x: event.clientX, y: event.clientY };
    setFocusedElement(null);
    setLabelPosition(null);
  };

  const handleMouseUp = (event) => {
    const threshold = 5;
    const dx = event.clientX - dragStart.current.x;
    const dy = event.clientY - dragStart.current.y;
    const distance = Math.sqrt(dx * dx + dy * dy);

    if (distance <= threshold) {
      if (currentElement) {
        setFocusedElement(currentElement);
      } else {
        setFocusedElement(null);
      }
      const { offsetX, offsetY } = event.nativeEvent;
      setLabelPosition({ x: offsetX + playerOffset, y: offsetY });
    }
  };

  useEffect(() => {
    // console.log("🔍 focus effect triggered, currentElement =", currentElement);

    // if nothing to focus, bail out
    if (!currentElement) {
      clearTimeout(focusTimeoutRef.current);
      setFocusedElement(null);
      setLabelPosition(null);
      setLabelExpanded(false);
      return;
    }

    // clear any old timer
    clearTimeout(focusTimeoutRef.current);

    // schedule focus
    focusTimeoutRef.current = setTimeout(() => {
      // console.log("✔️ focus timer fired");
      setFocusedElement(currentElement);
      setLabelExpanded(false);
      const { x, y } = latestMousePositionRef.current;
      setLabelPosition({ x, y });
    }, 300);

    return () => {
      clearTimeout(focusTimeoutRef.current);
    };
  }, [currentElement]);

  useEffect(() => {
    // console.log(
    //   "⏱️ expand effect triggered, focusedElement =",
    //   focusedElement,
    //   "labelPosition =",
    //   labelPosition
    // );

    // only expand once you have both a focus and a position
    if (!focusedElement || !labelPosition) {
      clearTimeout(expandedTimeoutRef.current);
      setLabelExpanded(false);
      return;
    }

    // clear any previous expand timer & collapse
    clearTimeout(expandedTimeoutRef.current);
    setLabelExpanded(false);

    // schedule expand
    expandedTimeoutRef.current = setTimeout(() => {
      // console.log("➡️ expand timer fired — setting labelExpanded = true");
      setLabelExpanded(true);
    }, 1050);

    return () => {
      clearTimeout(expandedTimeoutRef.current);
    };
  }, [focusedElement, labelPosition]);

  const doneRecording = () => {
    sendMessageToUnity("EventDispatcher", "RecordingStopped");
  };

  const assessmentComplete = () => {
    sendMessageToUnity("EventDispatcher", "CompleteAssessment");
  };

  const changeEnvironment = (environmentId) => {
    console.log("Changing env to" + environmentId);
    sendMessageToUnity("EventDispatcher", "ChangeEnvironment", environmentId);
  };

  const startGuide = () => {
    sendMessageToUnity("EventDispatcher", "LoadGuide", experienceData.guideId);
  };

  const rotateCamera = (direction) => {
    sendMessageToUnity("EventDispatcher", "RotateCamera", direction);
  };

  const isChrome = true;
  //  typeof window !== "undefined" &&
  //  /Chrome/.test(navigator.userAgent) &&
  //  /Google Inc/.test(navigator.vendor);

  const overlayStyles = {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(255, 255, 255, 0.8)",
    zIndex: 1000,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
  };

  const getDisplayName = (elementData) => {
    return !elementData.targetEnvironmentID
      ? `${elementData.labelData.displayName}`
      : `${elementData.labelData.displayName} 🔍`;
  };

  const getSubtitle = (elementData) => {
    return elementData.targetEnvironmentID && environmentsVisited < 6
      ? "Scroll or pinch to zoom in."
      : null;
  };

  // Check if the user is on a mobile device
  const isMobile =
    typeof window !== "undefined" &&
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );

  // If mobile, display a message and don't load the experience
  if (isMobile) {
    return (
      <div
        style={{
          position: "relative",
          width: "100vw",
          height: "100vh",
          minHeight: "100dvh",
          backgroundColor: "#000",
          overflow: "hidden",
        }}
      >
        <iframe
          src="https://player.vimeo.com/video/1158952185?autoplay=1&title=0&byline=0&portrait=0&muted=1&loop=1&background=1&playsinline=1"
          title="10k Science preview"
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            width: "177.78vh", // 16:9 cover for tall viewports
            height: "100vh",
            minWidth: "100vw",
            minHeight: "56.25vw", // 9/16 of viewport width
            transform: "translate(-50%, -50%)",
            border: 0,
          }}
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
        />
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            color: "#fff",
            paddingTop: `calc(env(safe-area-inset-top, 0px) + ${
              isPhone ? 12 : 16
            }px)`,
            paddingBottom: isPhone ? "12px" : "16px",
            paddingLeft: isPhone ? "14px" : "20px",
            paddingRight: isPhone ? "14px" : "20px",
            borderRadius: 0,
            width: "100%",
            boxSizing: "border-box",
            overflowWrap: "break-word",
            textAlign: "left",
            boxShadow: "0 12px 28px rgba(0, 0, 0, 0.4)",
            border: "1px solid rgba(255, 255, 255, 0.08)",
          }}
        >
          <Typography variant="h5" sx={{ mb: 1 }}>
            Hi there!
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.9 }}>
            Thanks for your interest in the{" "}
            <a
              href="https://10k.science"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: "#7bd0ff",
                textDecoration: "none",
                fontWeight: 600,
              }}
            >
              10k Science
            </a>
            {" "}
            Web Viewer. The live viewer is optimized for desktops; mobile support is
            coming soon.
          </Typography>
        </Box>
      </div>
    );
  }

  return (
    <div
      className="content"
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
    >
      {showEnvironmentLabel && (
        <Box
          component={animated.div}
          sx={{
            position: "absolute",
            bottom: envLabelBottom,
            left: `calc(${playerOffset}px + 50% - ${playerOffset / 2}px)`,
            transform: "translateX(-50%)",
            color: "white",
            padding: overlayPadding,
            borderRadius: "8px",
            fontSize: "18px",
            maxWidth: overlayWidth,
            opacity: showEnvironmentLabel ? 1 : 0,
            transition: "opacity 500ms ease",
            zIndex: 20,
            pointerEvents: "none",
            textAlign: "center",
            textShadow: "0px 0px 10px rgba(0, 0, 0, 0.5)",
          }}
        >
          <Typography
            variant="caption"
            sx={{ fontSize: isPhone ? "1rem" : "1.2rem" }}
          >
            LOCATION
          </Typography>
          <Typography
            variant="h4"
            sx={{ fontSize: isPhone ? "1.6rem" : "2.2rem" }}
          >
            {currentEnvironment?.environmentName}
          </Typography>
        </Box>
      )}

      {instructionsVisible && (
        <Box
          component={animated.div}
          sx={{
            position: "absolute",
            top: topMessageOffset,
            left: `calc(${playerOffset}px + 50% - ${playerOffset / 2}px)`,
            transform: "translateX(-50%)",
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            color: "#fff",
            padding: overlayPadding,
            maxWidth: overlayWidth,
            borderRadius: "10px",
            pointerEvents: "none",
            opacity: instructionsVisible ? 1 : 0,
            transition: "opacity 500ms ease",
            zIndex: 20,
            pointerEvents: "none",
            textAlign: "center",
            textShadow: "0px 0px 10px rgba(0, 0, 0, 0.5)",
          }}
        >
          <Typography variant="caption">INSTRUCTION</Typography>
          <Typography variant="h5" sx={{ fontSize: isPhone ? "1.1rem" : undefined }}>
            {currentInsight?.endConditions[0]?.name}
          </Typography>
        </Box>
      )}

      {showControlsOverlay && (
        <Box
          component={animated.div}
          sx={{
            position: "absolute",
            left: "50%",
            bottom: "40px",
            transform: "translateX(-50%)",
            background: "rgba(0, 0, 0, 0.68)",
            color: "#fff",
            padding: isPhone ? "10px 14px" : "12px 18px",
            borderRadius: "14px",
            display: "flex",
            flexDirection: "column",
            gap: "4px",
            pointerEvents: "none",
            zIndex: 24,
            boxShadow: "0 10px 32px rgba(0, 0, 0, 0.35)",
            border: "1px solid rgba(255, 255, 255, 0.08)",
            textAlign: "center",
            letterSpacing: "0.01em",
            maxWidth: overlayWidth,
          }}
        >
          <Typography variant="overline" sx={{ opacity: 0.8 }}>
            Controls
          </Typography>
          <Typography variant="subtitle1">Scroll to zoom in</Typography>
          <Typography variant="subtitle1">Drag to look around</Typography>
        </Box>
      )}

      <div
        style={{
          position: "absolute",
          top: "var(--app-header-height, 0px)",
          left: `${playerOffset}px`,
          width: `calc(100% - ${playerOffset}px)`,
          height: "calc(100% - var(--app-header-height, 0px))",
        }}
      >
        <Unity
          unityProvider={unityProvider}
          style={{
            width: "100%",
            height: "100%",
            cursor: cursorStyle,
            mt: "50px",
          }}
          devicePixelRatio={window.devicePixelRatio}
          width="100%"
          height="100%"
        />
      </div>

      <Box
        className="edge-zone edge-zone-left"
        sx={{
          position: "absolute",
          top: "var(--app-header-height, 0px)",
          left: 0,
          width: edgeControlWidth,
          height: "calc(100% - var(--app-header-height, 0px))",
          zIndex: 15,
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          pointerEvents: edgeHover.left ? "auto" : "none",
        }}
      >
        <Box
          className="edge-button"
          component="button"
          type="button"
          aria-label="Rotate camera left"
          onClick={(event) => {
            event.stopPropagation();
            rotateCamera("-4,0");
          }}
          sx={{
            width: edgeControlWidth,
            height: "100%",
            border: "none",
            borderRadius: 0,
            backgroundColor: "rgba(0, 0, 0, 0.3)",
            color: "#fff",
            opacity: edgeHover.left ? 1 : 0,
            transition: "opacity 200ms ease, background-color 200ms ease",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "18px",
            "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.45)" },
          }}
        >
          {"◀"}
        </Box>
      </Box>

      <Box
        className="edge-zone edge-zone-right"
        sx={{
          position: "absolute",
          top: "var(--app-header-height, 0px)",
          right: 0,
          width: edgeControlWidth,
          height: "calc(100% - var(--app-header-height, 0px))",
          zIndex: 15,
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          pointerEvents: edgeHover.right ? "auto" : "none",
        }}
      >
        <Box
          className="edge-button"
          component="button"
          type="button"
          aria-label="Rotate camera right"
          onClick={(event) => {
            event.stopPropagation();
            rotateCamera("4,0");
          }}
          sx={{
            width: edgeControlWidth,
            height: "100%",
            border: "none",
            borderRadius: 0,
            backgroundColor: "rgba(0, 0, 0, 0.3)",
            color: "#fff",
            opacity: edgeHover.right ? 1 : 0,
            transition: "opacity 200ms ease, background-color 200ms ease",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "18px",
            "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.45)" },
          }}
        >
          {"▶"}
        </Box>
      </Box>

      <Box
        className="edge-zone edge-zone-top"
        sx={{
          position: "absolute",
          top: "var(--app-header-height, 0px)",
          left: 0,
          width: "100%",
          height: edgeControlHeight,
          zIndex: 15,
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "center",
          pointerEvents: edgeHover.top ? "auto" : "none",
        }}
      >
        <Box
          className="edge-button"
          component="button"
          type="button"
          aria-label="Rotate camera up"
          onClick={(event) => {
            event.stopPropagation();
            rotateCamera("0,2");
          }}
          sx={{
            width: "100%",
            height: edgeControlHeight,
            border: "none",
            borderRadius: 0,
            backgroundColor: "rgba(0, 0, 0, 0.3)",
            color: "#fff",
            opacity: edgeHover.top ? 1 : 0,
            transition: "opacity 200ms ease, background-color 200ms ease",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "18px",
            "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.45)" },
          }}
        >
          {"▲"}
        </Box>
      </Box>

      <Box
        className="edge-zone edge-zone-bottom"
        sx={{
          position: "absolute",
          bottom: 0,
          left: 0,
          width: "100%",
          height: edgeControlHeight,
          zIndex: 15,
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "center",
          pointerEvents: edgeHover.bottom ? "auto" : "none",
        }}
      >
        <Box
          className="edge-button"
          component="button"
          type="button"
          aria-label="Rotate camera down"
          onClick={(event) => {
            event.stopPropagation();
            rotateCamera("0,-2");
          }}
          sx={{
            width: "100%",
            height: edgeControlHeight,
            border: "none",
            borderRadius: 0,
            backgroundColor: "rgba(0, 0, 0, 0.3)",
            color: "#fff",
            opacity: edgeHover.bottom ? 1 : 0,
            transition: "opacity 200ms ease, background-color 200ms ease",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "18px",
            "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.45)" },
          }}
        >
          {"▼"}
        </Box>
      </Box>

      {embed ? null : (
        <Sidebar
          focusedElement={focusedElement}
          currentEnvironment={currentEnvironment}
          system={systemData}
        />
      )}

      <Box
        component="div"
        sx={{
          position: "absolute",
          transform: focusedElement ? "translateY(0px)" : "translateY(8px)",
          opacity: focusedElement ? 1 : 0,
          transition: "opacity 300ms ease, transform 300ms ease",
          pointerEvents: "none",
          zIndex: 10,
          visibility: labelPosition ? "visible" : "hidden", // optional: to hide before first move
        }}
      >
        <div>{buildPrefix}</div>
        {focusedElement && (
          <Label
            key={focusedElement.id}
            labelPosition={labelPosition}
            displayName={getDisplayName(focusedElement)}
            description={focusedElement.labelData.description}
            subtitle={getSubtitle(focusedElement)}
            alignment="auto"
            offsetLeft={0}
            expanded={labelExpaneded}
          />
        )}
      </Box>

      {systemData && (
        <EnvironmentMap
          systemData={systemData}
          currentEnvironmentId={currentEnvironment?.id}
          changeEnvironment={changeEnvironment}
          layoutOverrides={layoutOverrides}
        />
      )}

      <VoiceRecorder
        doneRecording={doneRecording}
        assessmentComplete={assessmentComplete}
        ref={voiceRecorderRef}
      />

      {/* {!systemLoaded && (
        <div style={overlayStyles}>
          <div>
            <div className="fullscreen-loader">
              <DustParticles />
              <Box sx={{ mr: "5%" }}>
                <img
                  src={logo}
                  alt="Logo"
                  style={{ width: "100%", maxWidth: "400px" }}
                />
              </Box>
              {isChrome ? (
                <>
                  <Box style={{ width: "200px", maxWidth: "400px" }}>
                    {!systemData ? (
                      <LoadingAnimation />
                    ) : (
                      <LoadingSequence systemData={systemData} />
                    )}
                  </Box>
                </>
              ) : (
                <Box width="40%" color="white" sx={{ mr: "80px", mt: "20px" }}>
                  <Box sx={{ position: "relative", paddingTop: "56.25%" }}>
                    <iframe
                      src="https://player.vimeo.com/video/1073383527?autoplay=1&title=0&byline=0&portrait=0&muted=1&loop=1"
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        border: 0,
                      }}
                      allow="autoplay; fullscreen; picture-in-picture"
                      allowFullScreen
                    />
                  </Box>
                  <Box sx={{ m: "10%" }}>
                    {" "}
                    <Typography variant="h4" gutterBottom>
                      Hi there!
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      Thank you for visiting. This experience uses advanced web
                      features currently available only in Google Chrome. Please
                      switch to Chrome for the best experience. We appreciate
                      your understanding.
                    </Typography>
                  </Box>
                </Box>
              )}
            </div>
          </div>
        </div>
      )} */}
      {systemLoaded && <StartGuideDialog startGuide={startGuide} />}
      { <MicAccessPopup />}
    </div>
  );
};

export default UnityWebViewer;
